/**
 * Locale selection for texts the backend renders for a recipient (e-mails).
 *
 * The backend has no process-wide active locale: every render receives the
 * locale explicitly. German is the default, as in the frontend facade.
 *
 * Note: `resolveSupportedLocale` in `@atoms/i18n-module` does the same
 * primary-language matching for the browser; the two can be merged once the
 * i18n foundation allows a shared React-free home.
 */

/** Locales the framework ships backend texts for. */
export const EMAIL_LOCALES = ["de-DE", "en-US"] as const;

export type EmailLocale = (typeof EMAIL_LOCALES)[number];

export const DEFAULT_EMAIL_LOCALE: EmailLocale = "de-DE";

const WEIGHT = /^\s*q\s*=\s*(\d(?:\.\d{0,3})?)\s*$/i;

/** The `q` weight of an entry's parameters: 1 when absent, null when malformed or repeated. */
function parseWeight(params: readonly string[]): number | null {
  if (params.length > 1) {
    return null;
  }
  const [param] = params;
  if (param === undefined) {
    return 1;
  }
  const weight = Number(WEIGHT.exec(param)?.[1] ?? Number.NaN);
  return weight >= 0 && weight <= 1 ? weight : null;
}

/**
 * Language tags of an `Accept-Language` header, best first. Ties keep header
 * order. Malformed entries and `q=0` are dropped; `*` is kept.
 *
 * Accept-Language is a best-effort hint; further RFC 9110 parser corner cases
 * get a verdict reply, not a fix.
 */
export function parseAcceptLanguage(
  header: string | null | undefined,
): string[] {
  if (!header) {
    return [];
  }
  const entries: { tag: string; q: number; index: number }[] = [];
  for (const [index, part] of header.split(",").entries()) {
    const [rawTag, ...params] = part.trim().split(";");
    const tag = rawTag?.trim() ?? "";
    if (tag !== "*" && !/^[A-Za-z]{1,8}(-[A-Za-z0-9]{1,8})*$/.test(tag)) {
      continue;
    }
    const q = parseWeight(params);
    if (q !== null && q > 0) {
      entries.push({ tag, q, index });
    }
  }
  entries.sort((a, b) => b.q - a.q || a.index - b.index);
  return entries.map((entry) => entry.tag);
}

/**
 * Maps one requested tag onto a supported locale: exact match first, then
 * the same primary language ("de" or "de-AT" gives "de-DE").
 */
export function pickSupportedLocale<L extends string>(
  requested: string | null | undefined,
  supported: readonly L[],
): L | undefined {
  if (!requested) {
    return undefined;
  }
  const wanted = requested.toLowerCase();
  const exact = supported.find((locale) => locale.toLowerCase() === wanted);
  if (exact) {
    return exact;
  }
  const primary = wanted.split("-")[0];
  return supported.find(
    (locale) => locale.toLowerCase().split("-")[0] === primary,
  );
}

/**
 * The locale to render an e-mail in. `requested` is an explicit locale tag or
 * a full `Accept-Language` header; the first entry that matches a shipped
 * locale wins, otherwise German. Only pass a request-derived value for a
 * mail the requester receives himself.
 */
export function resolveEmailLocale(
  requested: string | null | undefined,
): EmailLocale {
  for (const tag of parseAcceptLanguage(requested)) {
    // "Anything is acceptable" at the best weight: the default applies.
    if (tag === "*") {
      return DEFAULT_EMAIL_LOCALE;
    }
    const picked = pickSupportedLocale(tag, EMAIL_LOCALES);
    if (picked) {
      return picked;
    }
  }
  return DEFAULT_EMAIL_LOCALE;
}
